/**
 * Iter 31.155 — RunawayBanner.
 *
 * Listens for the `shed:runaway` event dispatched by the axios
 * RequestRateGuard in `/app/frontend/src/lib/api.js`. When a runaway
 * is detected on any endpoint, surfaces a top-right corner banner
 * with the offending path, the hit count, and the remaining cooldown.
 *
 * Mounted once at the root (App.js) — globally visible regardless of
 * which page is active. Dismissible. Self-clears when the cooldown
 * expires.
 */
import React, { useEffect, useState } from "react";

export default function RunawayBanner() {
    const [event, setEvent] = useState(null);
    const [remainingMs, setRemainingMs] = useState(0);

    useEffect(() => {
        const onRunaway = (e) => {
            const d = e.detail || {};
            setEvent(d);
            const until = d.cooldown_until || (Date.now() + (d.cooldown_ms || 5_000));
            setRemainingMs(Math.max(0, until - Date.now()));
        };
        window.addEventListener("shed:runaway", onRunaway);
        return () => window.removeEventListener("shed:runaway", onRunaway);
    }, []);

    useEffect(() => {
        if (!event) return undefined;
        const until = event.cooldown_until || (Date.now() + (event.cooldown_ms || 5_000));
        const tick = () => {
            const left = until - Date.now();
            if (left <= 0) {
                setEvent(null);
                setRemainingMs(0);
                return;
            }
            setRemainingMs(left);
        };
        const id = setInterval(tick, 250);
        return () => clearInterval(id);
    }, [event]);

    if (!event) return null;

    return (
        <div
            data-testid="runaway-banner"
            className="!fixed top-3 right-3 z-[9999] max-w-md paper-card border-2 border-stamp-red bg-paper text-ink shadow-lg p-3 font-body text-xs"
            role="alert"
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div
                        className="font-display text-stamp-red text-sm tracking-widest mb-1"
                        data-testid="runaway-banner-title"
                    >
                        RUNAWAY DETECTED — THROTTLED
                    </div>
                    <div className="text-ink/80">
                        <code className="font-mono">{event.endpoint}</code>{" "}
                        fired{" "}
                        <span className="font-bold text-stamp-red" data-testid="runaway-banner-count">
                            {event.count}×
                        </span>{" "}
                        in {Math.round((event.window_ms || 0) / 1000)}s. Network calls
                        to this endpoint are blocked for{" "}
                        <span data-testid="runaway-banner-cooldown">
                            {Math.ceil(remainingMs / 1000)}s
                        </span>
                        .
                    </div>
                    <div className="text-ink/50 mt-1 italic">
                        Likely a useEffect dep-array bug (object ref churn) or a
                        runaway interval. Check DevTools console for the path.
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setEvent(null)}
                    className="text-ink/40 hover:text-stamp-red text-base leading-none"
                    aria-label="dismiss"
                    data-testid="runaway-banner-dismiss"
                >
                    ×
                </button>
            </div>
        </div>
    );
}
